
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { format } from 'date-fns';

type TimeframeType = 'daily' | 'weekly' | 'monthly' | 'yearly';

interface SalesDetailsDialogProps {
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  totalAmount: number | undefined;
  policies: any[];
  timeframe: TimeframeType;
}

export const SalesDetailsDialog: React.FC<SalesDetailsDialogProps> = ({
  title,
  open,
  onOpenChange,
  totalAmount = 0,
  policies = [],
  timeframe
}) => {
  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '$0';
    return `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  };

  // We need to properly calculate premiums without double counting
  // Create a map to track unique policies by ID
  const uniquePolicies = new Map();
  
  // First, add all policies to our map
  policies.forEach(policy => {
    uniquePolicies.set(policy.id, {
      ...policy,
      // Initialize endorsement premium if not present
      endorsement_premium: policy.endorsement_premium || 0
    });
  });
  
  // Calculate totals from our unique policies map
  let basePremium = 0;
  let endorsementPremium = 0;
  
  uniquePolicies.forEach(policy => {
    basePremium += Number(policy.premium_amount) || 0;
    endorsementPremium += Number(policy.endorsement_premium) || 0;
  });
  
  // Calculate total (should match the passed-in total)
  const calculatedTotal = basePremium + endorsementPremium;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <div className="mb-4">
            <div className="flex flex-col gap-1">
              <p className="text-lg font-semibold">
                Total: {formatCurrency(calculatedTotal)}
              </p>
              <div className="text-sm text-gray-600">
                <p>Base Premium: {formatCurrency(basePremium)}</p>
                <p>Endorsement Premium: {formatCurrency(endorsementPremium)}</p>
                {Math.abs((totalAmount || 0) - calculatedTotal) > 1 && (
                  <p className="text-xs text-gray-400 mt-1">
                    Note: Total represents unique policies and endorsements to avoid double counting
                  </p>
                )}
              </div>
            </div>
          </div>
          
          {policies.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Policy #</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Producer</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Carrier</TableHead>
                    <TableHead className="text-right">Base Premium</TableHead>
                    <TableHead className="text-right">Endorsement</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Array.from(uniquePolicies.values()).map((policy) => {
                    const basePremium = Number(policy.premium_amount) || 0;
                    const endorsement = Number(policy.endorsement_premium) || 0;
                    const total = basePremium + endorsement;
                    
                    return (
                      <TableRow key={policy.id}>
                        <TableCell>
                          {policy.sales_date ? format(new Date(policy.sales_date), 'MM/dd/yyyy') : 'N/A'}
                          {policy.endorsement_date && policy.endorsement_premium ? (
                            <div className="text-xs text-gray-500 mt-1">
                              Endorsement: {format(new Date(policy.endorsement_date), 'MM/dd/yyyy')}
                            </div>
                          ) : null}
                        </TableCell>
                        <TableCell>{policy.policy_number || 'N/A'}</TableCell>
                        <TableCell>
                          {policy.customer_first_name && policy.customer_last_name 
                            ? `${policy.customer_first_name} ${policy.customer_last_name}`
                            : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {policy.producer_name 
                            ? policy.producer_name 
                            : policy.producer?.first_name && policy.producer?.last_name
                              ? `${policy.producer.first_name} ${policy.producer.last_name}`
                              : 'Unknown Producer'}
                        </TableCell>
                        <TableCell>{policy.insurance_type || 'N/A'}</TableCell>
                        <TableCell>{policy.insurance_carrier || 'N/A'}</TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(basePremium)}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(endorsement)}
                        </TableCell>
                        <TableCell className="text-right font-medium text-purple-700">
                          {formatCurrency(total)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-gray-500 italic">
              No policies found for this {timeframe.replace('ly', '')} period.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
